import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { PASSWORD_MIN } from '@champdle/contracts';
import { ApiRequestError } from '../api.js';
import { useAccountActions } from '../queries.js';
import type { View } from '../route.js';

interface Props {
  mode: 'login' | 'register';
  hasGuestGames: boolean;
  go: (view: View) => void;
}

export function AuthForm({ mode, hasGuestGames, go }: Props) {
  const { login, register } = useAccountActions();
  const action = mode === 'login' ? login : register;
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => headingRef.current?.focus(), [mode]);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (action.isPending) return;
    const done = { onSuccess: () => go('play') };
    if (mode === 'login') login.mutate({ username, password }, done);
    else register.mutate({ username, password, ...(email.trim() ? { email } : {}) }, done);
  };

  const error = action.error;
  const message =
    error instanceof ApiRequestError
      ? error.message
      : error
        ? 'Something went wrong. Please try again.'
        : null;

  const login_ = mode === 'login';
  return (
    <section className="panel" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} ref={headingRef} tabIndex={-1}>
        {login_ ? 'Log in' : 'Create an account'}
      </h2>
      <p className="muted">
        {login_
          ? hasGuestGames
            ? 'The games you played as a guest on this device will be added to your account.'
            : 'Welcome back.'
          : hasGuestGames
            ? 'Your games so far stay with you. They become part of your account.'
            : 'An account keeps your history and stats across devices. Playing never requires one.'}
      </p>
      <form className="form" onSubmit={submit} noValidate>
        <label htmlFor={`${id}-u`}>Username</label>
        <input
          id={`${id}-u`}
          name="username"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          aria-describedby={login_ ? undefined : `${id}-uh`}
        />
        {login_ ? null : (
          <p id={`${id}-uh`} className="hint">
            3 to 20 letters, numbers or underscores.
          </p>
        )}

        <label htmlFor={`${id}-p`}>Password</label>
        <input
          id={`${id}-p`}
          name="password"
          type="password"
          autoComplete={login_ ? 'current-password' : 'new-password'}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-describedby={login_ ? undefined : `${id}-ph`}
        />
        {login_ ? null : (
          <p id={`${id}-ph`} className="hint">
            At least {PASSWORD_MIN} characters. A few words in a row works well.
          </p>
        )}

        {login_ ? null : (
          <>
            <label htmlFor={`${id}-e`}>Email (optional)</label>
            <input
              id={`${id}-e`}
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={`${id}-eh`}
            />
            <p id={`${id}-eh`} className="hint">
              Not needed to play. There is no password reset yet, so if you forget your password the
              account cannot be recovered.
            </p>
          </>
        )}

        {message ? (
          <p className="form__error" role="alert">
            {message}
          </p>
        ) : null}

        <div className="actions">
          <button type="submit" className="primary" disabled={action.isPending}>
            {action.isPending ? 'Please wait…' : login_ ? 'Log in' : 'Create account'}
          </button>
          <button type="button" className="link" onClick={() => go('play')}>
            Back to the game
          </button>
        </div>
      </form>
      <p className="muted">
        {login_ ? 'No account yet? ' : 'Already have one? '}
        <button type="button" className="link" onClick={() => go(login_ ? 'register' : 'login')}>
          {login_ ? 'Create an account' : 'Log in'}
        </button>
      </p>
    </section>
  );
}
